'use client';

import { useMediaQuery } from '@/components/core';
import { UIComponent } from '@/components/core/components';
import ResultGrid from '@/components/core/components/molecules/result-grid';
import Feed from '@/components/core/components/organism/feed';
import { FeedGroupContext } from '@/components/core/components/organism/feed/components/context/context';
import { useCallback, useMemo, useState } from 'react';

import { useUser } from '@/components/context/user';
import { gql, paginationToArray } from '@/services/api/namespaces/_utils';
import { UserType } from '@/@types';
import * as styles from './activity.styles';
import { UserRecommendationsFragment } from '../profile.queries';

const ProfileActivity: UIComponent<{ user: UserType }> = ({ user }) => {
  const { api } = useUser();
  const [playing, setPlaying] = useState<string | null>(null);
  const [content, setContent] = useState(paginationToArray(user.recommendations));
  const [pageInfo, setPageInfo] = useState(user.recommendations!.pageInfo);
  const mediaQuery = useMediaQuery();
  const contextValue = useMemo(() => {
    return {
      playing,
    };
  }, [playing]);

  const onLoadMore = useCallback(async () => {
    const page = (
      await api.graphqlRequest<{ User: { get: UserType } }>({
        query: gql`
          ${UserRecommendationsFragment}
          query getUserRecommendations($id: ID!, $after: ID) {
            User {
              get(id: $id) {
                ...UserRecommendations
              }
            }
          }
        `,
        variables: {
          id: user.id,
          after: pageInfo?.endCursor,
        },
      })
    ).data.User.get.recommendations;

    const more = paginationToArray(page);
    setPageInfo(page?.pageInfo);
    setContent((x) => {
      return x.concat(more || []);
    });
  }, [api, pageInfo?.endCursor, user.id]);

  return (
    <FeedGroupContext.Provider value={contextValue}>
      <ResultGrid maxCols={3} itemClassName={styles.feed} hasMore={pageInfo?.hasNextPage} onLoadMore={onLoadMore}>
        {content.map((recommendation) => {
          const hasVideo = recommendation.media.some((media) => {
            return media.type === 'video';
          });
          return (
            <Feed
              {...(hasVideo && {
                onMouseEnter: () => {
                  if (hasVideo) {
                    setPlaying(recommendation.product.id);
                  }
                },
                onMouseLeave: () => {
                  setPlaying(null);
                },
                onViewport: (isOnViewport) => {
                  if (isOnViewport && hasVideo && !mediaQuery.up('md')) {
                    setPlaying(recommendation.product.id);
                  }
                },
              })}
              key={recommendation.id}
              recommendation={{ ...recommendation, user }}
              product={recommendation.product}
              collection={recommendation.collection}
            />
          );
        })}
      </ResultGrid>
    </FeedGroupContext.Provider>
  );
};

export default ProfileActivity;
